import {
  Brush,
  Check,
  Layers,
  MessageSquare,
  Package,
  ShoppingBag,
  Truck,
  Workflow,
} from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: <Brush className="size-6 text-purple-400" />,
      title: 'DESIGN & PRODUCTION',
      description:
        'AI-assisted design generation, brand-consistent variants, and mockup automation across thousands of SKUs — turning a concept into a marketplace-ready listing in days, not weeks.',
      demo: (
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center mr-3">
              <Brush size={20} />
            </div>
            <div>
              <p className="font-medium">Design Pipeline</p>
              <p className="text-sm text-gray-300">Live brand products</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              { src: '/images/elzary-1.webp', alt: 'Elzary — apparel product from the AITOS portfolio' },
              { src: '/images/avyraatlas-1.webp', alt: 'Avyra Atlas — lifestyle product from the AITOS portfolio' },
              { src: '/images/avyraatlas-2.webp', alt: 'Avyra Atlas — lifestyle product variant from the AITOS portfolio' },
            ].map((variant) => (
              <div
                key={variant.src}
                className="aspect-square rounded-md overflow-hidden border border-purple-500/20 bg-gray-900"
              >
                <img
                  src={variant.src}
                  alt={variant.alt}
                  loading="lazy"
                  className="size-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      ),
      bullets: [
        'Text-to-image, brand-consistent variants',
        'Bulk mockup generation, ready for listing',
      ],
    },
    {
      icon: <Layers className="size-6 text-purple-400" />,
      title: 'STOREFRONT & DISTRIBUTION',
      description:
        'Each brand runs on our in-house storefront (custom code, owned end-to-end), with SEO-optimized listings distributed to Etsy, Amazon, and eBay. Daily price and inventory sync across every channel.',
      demo: (
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center mr-3">
                <Workflow size={20} />
              </div>
              <div>
                <p className="font-medium">Storefront + Marketplaces</p>
                <div className="flex items-center">
                  <Check size={16} className="text-green-500 mr-1" />
                  <span className="text-sm text-green-500">Live</span>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {[
              { name: 'In-house Storefront', tag: 'Custom' },
              { name: 'Etsy', tag: 'Synced' },
              { name: 'Amazon', tag: 'Synced' },
              { name: 'eBay', tag: 'Synced' },
            ].map((mp) => (
              <div key={mp.name} className="bg-gray-700 rounded-lg p-2 flex items-center text-sm">
                <ShoppingBag size={16} className="text-purple-400 mr-2" />
                <span>{mp.name}</span>
                <span className="ml-auto text-xs text-green-500">{mp.tag}</span>
              </div>
            ))}
          </div>
        </div>
      ),
      bullets: [
        'In-house storefront stack (custom code, fully owned)',
        'SEO listing copy + tags tuned per marketplace; daily price/inventory sync',
      ],
    },
    {
      icon: <Truck className="size-6 text-purple-400" />,
      title: 'FULFILLMENT & LOGISTICS',
      description:
        'Order routing across Printify, Printful, and Gelato; tracking sync, exception handling, and SLA monitoring. Customers get reliable delivery; we get fewer 2 a.m. fires.',
      demo: (
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-500 to-blue-500 flex items-center justify-center mr-3">
              <Package size={20} />
            </div>
            <div>
              <p className="font-medium">Fulfillment Router</p>
              <p className="text-sm text-gray-300">SLA: 98.5% on-time</p>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-gray-300">
              <span>Printify</span>
              <span className="text-green-400">42%</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span>Printful</span>
              <span className="text-green-400">36%</span>
            </div>
            <div className="flex justify-between text-gray-300">
              <span>Gelato</span>
              <span className="text-green-400">22%</span>
            </div>
          </div>
        </div>
      ),
      bullets: [
        'Multi-partner routing per SKU and region',
        'Tracking sync + exception triage on autopilot',
      ],
    },
    {
      icon: <MessageSquare className="size-6 text-purple-400" />,
      title: 'CUSTOMER EXPERIENCE & GROWTH',
      description:
        'AI-assisted CX with sub-60-second first responses, reviews ops, retention flows, and paid + organic growth loops. The brand stays a brand — not a help desk.',
      demo: (
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="space-y-3">
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <span className="text-xs font-semibold inline-block text-purple-400">
                  First Response
                </span>
                <span className="text-xs font-semibold inline-block text-purple-400">{'<60s'}</span>
              </div>
              <div className="overflow-hidden h-2 rounded bg-gray-700">
                <div className="w-[92%] h-full bg-purple-500" />
              </div>
            </div>
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <span className="text-xs font-semibold inline-block text-purple-400">CSAT</span>
                <span className="text-xs font-semibold inline-block text-purple-400">4.8/5</span>
              </div>
              <div className="overflow-hidden h-2 rounded bg-gray-700">
                <div className="w-[96%] h-full bg-purple-500" />
              </div>
            </div>
          </div>
        </div>
      ),
      bullets: [
        'LLM-drafted replies reviewed by humans',
        'Retention flows, reviews ops, growth loops',
      ],
    },
  ];

  return (
    <section className="py-20 px-6 bg-black" id="features">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-purple-400 text-xl mb-4 tracking-widest">OUR OPERATIONAL SCOPE</h2>
          <h3 className="text-5xl font-bold mb-3">
            Everything between an empty domain and a live brand.
          </h3>
          <p className="text-gray-300 max-w-3xl mx-auto">
            AITOS Labs LLC operates POD brands end-to-end. The same stack runs across our portfolio
            (Elzary, Avyra Atlas) and our managed-service engagements.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {features.map((feature, index) => (
            <div
              key={`${index + 1}`}
              className="bg-gray-900 rounded-xl p-8 hover:transform hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 flex flex-col h-full"
            >
              <div className="flex-grow">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    {feature.icon}
                  </div>
                  <h4 className="text-3xl font-bold">{feature.title}</h4>
                </div>
                <p className="text-gray-300 mb-6">{feature.description}</p>

                {feature.bullets && (
                  <ul className="space-y-3 mb-8">
                    {feature.bullets.map((bullet, bulletIndex) => (
                      <li key={`bullet-${bulletIndex + 1}`} className="flex items-start">
                        <div className="size-1.5 rounded-full bg-purple-400 mt-2 mr-3 flex-shrink-0" />
                        <span className="text-gray-300 text-sm md:text-base">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="mt-6">{feature.demo}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;
